<template>
  <div>
    <div class="flex items-center justify-end mb-4" v-if="searchable">
      <input type="text" v-model="searchQuery" @keyup.enter="performSearch"
        :placeholder="searchPlaceholder ?? 'Search...'" class="w-1/3 p-2 mr-1 border rounded-md focus:outline-none" />
      <button @click="performSearch" class="h-full px-2 py-2 text-white bg-black rounded-md">
        <MagnifyingGlassIcon class="size-6" />
      </button>
    </div>

    <div class="relative w-full overflow-x-auto bg-surface rounded-lg shadow-md datatable h-max">
      <div v-if="isLoading || isFetching" class="absolute inset-0 z-10 flex items-center justify-center bg-surface">
        <div class="flex flex-col items-center gap-2">
          <div class="w-8 h-8 border-b-2 border-ink rounded-full animate-spin"></div>
          <span class="text-ink-soft">{{ isLoading ? 'Loading...' : 'Updating...' }}</span>
        </div>
      </div>
      <table class="w-full border-collapse">
        <thead class="bg-primary-light">
          <tr v-if="validColumns.length > 0">
            <th v-for="col in validColumns" :key="col.key" @click="() => sort(col)"
              :class="{ 'cursor-pointer': col.sortable }"
              class="p-3 text-ink text-base font-semibold border-r-[1px] border-white last-of-type:border-r-0">
              <div class="flex items-center gap-1 text-base">
                <template v-if="col.sortable">
                  <component :is="getSortIcon(col.sortKey)" class="w-4 h-4 min-w-4 min-h-4" />
                </template>
                {{ col.label }}
              </div>
            </th>
            <th v-if="showActionsColumn" class="p-4 font-semibold text-left text-ink-soft">
              Actions
            </th>
          </tr>
          <tr v-else>
            <th colspan="100%" class="p-4 text-center">No columns available</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading && !isFetching">
            <td class="p-4 text-center" :colspan="showActionsColumn ? validColumns?.length + 1 : validColumns.length">
              <slot name="loading">
                <div class="flex items-center justify-center p-4">
                  <div class="w-6 h-6 border-b-2 border-ink rounded-full animate-spin"></div>
                  <span class="ml-2 text-ink-soft">Loading...</span>
                </div>
              </slot>
            </td>
          </tr>
          <tr v-else-if="error">
            <td class="p-4 text-center text-red-600"
              :colspan="showActionsColumn ? validColumns?.length + 1 : validColumns.length">
              <slot name="error">
                <span>Error loading data. Please try again.</span>
              </slot>
            </td>
          </tr>
          <template v-else-if="showPagination && paginatedData && paginatedData.length > 0">
            <tr v-for="row in paginatedData" :key="(row as any).id" class="border-t cursor-pointer hover:bg-surface-2"
              :data-row-id="row_id ? row[row_id] : ''" @contextmenu.prevent="handleRowRightClick($event, row)">
              <td v-for="col in validColumns" :key="col.key"
                class="p-4 text-ink-soft font-[500] text-sm border-line border-[1px] relative overflow-hidden"
                @click="(event) => handleCellClick(event, row, col)" :class="col.cellClass ? col.cellClass(row) : ''">
                <!-- Ripple span -->
                <span class="ripple"></span>
                <template v-if="typeof col.renderer === 'function'">
                  <component :is="col.renderer(row[col.key], row)" />
                </template>
                <component v-else-if="typeof col.renderer === 'string'" :is="col.renderer" />
                <span v-else>{{ row[col.key] }}</span>
              </td>
              <td v-if="showActionsColumn && row" class="p-4" :class="rowClass ? rowClass(row) : ''">
                <slot name="actions" :row="row"> </slot>
              </td>
            </tr>
          </template>
          <template v-else-if="!showPagination && rawData && rawData.length > 0">
            <tr v-for="row in rawData" :key="(row as any).id" class="border-t cursor-pointer hover:bg-surface-2">
              <td v-for="col in validColumns" :key="col.key"
                class="p-4 text-ink-soft font-[500] text-sm border-line border-[1px] relative overflow-hidden"
                @click="(event) => handleCellClick(event, row, col)">
                <!-- Ripple span -->
                <span class="ripple"></span>
                <component :is="getRenderer(col.renderer, row)" v-if="typeof col.renderer === 'function'" />
                <component :is="getRenderer(col.renderer, row)" v-else-if="typeof col.renderer === 'string'" />
                <span v-else>{{ (row as any)[col.key] }}</span>
              </td>
              <td v-if="showActionsColumn && row" class="p-4" :class="rowClass ? rowClass(row) : ''">
                <slot name="actions" :row="row"> </slot>
              </td>
            </tr>
          </template>
          <tr v-else class="border-t hover:bg-surface-2">
            <td class="p-4 text-ink-soft text-center text-sm"
              :colspan="showActionsColumn ? validColumns?.length + 1 : validColumns.length">
              No data to display
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 mt-6 bg-surface-2 rounded-lg"
      v-if="paginatedData && paginatedData.length && showPagination">
      <span class="text-sm text-ink-soft">
        Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ totalItems }} records
        <span v-if="isFetching" class="ml-2 text-ink-mute">(Refreshing...)</span>
      </span>
      <div class="flex flex-wrap items-center gap-3">
        <button v-if="currentPage > 1" @click="changePage(1)" :disabled="isLoading || isFetching"
          :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
          class="p-2 text-sm text-white bg-black border rounded-md">
          <ArrowLeftIcon class="w-4 h-4" />
        </button>
        <button v-if="currentPage > 1" @click="changePage(currentPage - 1)" :disabled="isLoading || isFetching"
          :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
          class="p-2 text-sm text-white bg-black border rounded-md">
          Prev
        </button>
        <button v-if="shouldShowEllipsisBefore" class="p-2 text-ink-mute cursor-default">
          ...
        </button>
        <button v-for="n in visiblePages" :key="n" @click="() => changePage(n)" :disabled="isLoading || isFetching"
          :class="{
            'font-bold bg-ink text-surface': n === currentPage,
            'text-ink': n !== currentPage,
            'opacity-50 cursor-not-allowed': isLoading || isFetching,
          }" class="px-4 py-2 text-sm border border-[#00000060] rounded-md">
          {{ n }}
        </button>
        <button v-if="shouldShowEllipsisAfter" class="p-2 text-ink-mute cursor-default">...</button>
        <button v-if="currentPage < totalPages" @click="changePage(currentPage + 1)" :disabled="isLoading || isFetching"
          :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
          class="p-2 text-sm text-white bg-black border rounded-md">
          Next
        </button>
        <button v-if="currentPage < totalPages" @click="changePage(totalPages)" :disabled="isLoading || isFetching"
          :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
          class="p-2 text-sm text-white bg-black border rounded-md">
          <ArrowRightIcon class="w-4 h-4" />
        </button>
        <div v-if="isFetching" class="flex items-center ml-2">
          <div class="w-4 h-4 border-b-2 border-ink rounded-full animate-spin"></div>
        </div>
      </div>
      <select v-model="pageSize" @change="updatePageSize" :disabled="isLoading || isFetching"
        :class="{ 'opacity-50 cursor-not-allowed': isLoading || isFetching }"
        class="p-2 text-ink-soft bg-surface border rounded-md">
        <option v-for="size in pageSizeOptions" :key="size" :value="size">
          {{ size }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DashboardRequest } from '@/models';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
} from '@heroicons/vue/24/solid';

import { computed, defineEmits, defineProps, PropType, ref, VNode, watch } from 'vue';

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  cellClass?: (row: any) => string;
  renderer?: (value: any, row: any) => string | number | VNode;
}

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  columns: Array as () => Column[],
  pageSizeOptions: {
    type: Array as () => number[],
    default: () => [5, 10, 20, 50],
  },
  isAsync: Boolean,
  totalItems: Number,
  showActionsColumn: Boolean,
  currentPaginationInfo: {
    type: Object as () => DashboardRequest,
    default: () => ({}) as DashboardRequest,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: 'false',
    required: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  error: {
    type: [Error, String, null],
    default: null,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  showPagination: {
    type: Boolean,
    default: true,
  },
  row_id: {
    type: String,
    default: undefined,
  },
  rowClass: {
    type: Function as PropType<(row: any) => string>,
    default: () => (row: any) => '',
  },
});

const emit = defineEmits<{
  (e: 'fetch-data', data: any): void;
  (e: 'row-click', row: any): void;
  (e: 'cell-click', data: { row: any; column: Column }): void;
  (e: 'row-right-click', payload: { event: MouseEvent; row: any }): void;
}>();

const currentPage = ref(props.currentPaginationInfo ? props.currentPaginationInfo.page : 1);
const pageSize = ref(props.currentPaginationInfo ? props.currentPaginationInfo.per_page : 10);
const sortColumn = ref<string | null>(
  props.currentPaginationInfo ? props.currentPaginationInfo.sort_by : null
);
const sortOrder = ref<'asc' | 'desc'>(
  props.currentPaginationInfo ? props.currentPaginationInfo.sort_order : 'asc'
);

const handleRowRightClick = (event: MouseEvent, row: any) => {
  event.stopPropagation();
  emit('row-right-click', { event, row });
};

const searchQuery = ref(props.currentPaginationInfo ? props.currentPaginationInfo.search : '');
const shouldShowEllipsisBefore = computed(() => currentPage.value > 7);
const shouldShowEllipsisAfter = computed(() => currentPage.value < totalPages.value - 7);

watch(
  () => props.totalItems as number,
  (newTotal) => {
    const totalPages = Math.ceil((newTotal || 0) / (pageSize.value || 1));

    if (currentPage.value > totalPages) {
      changePage(Math.max(1, totalPages));
    }
  }
);

const performSearch = () => {
  emit('fetch-data', {
    searchQuery: searchQuery.value,
    page: 1,
    pageSize: pageSize.value,
    sortColumn: sortColumn.value,
    sortOrder: sortOrder.value,
  });
};
const validColumns = computed<Column[]>(() => {
  return Array.isArray(props.columns)
    ? props.columns.filter((column): column is Column => !!column)
    : [];
});
const sortedData = computed(() => {
  if (!sortColumn.value) return props.data;
  return props.data
    ? [...props.data].sort((a: any, b: any) => {
      const result = a[sortColumn.value!] > b[sortColumn.value!] ? 1 : -1;
      return sortOrder.value === 'asc' ? result : -result;
    })
    : [];
});

const paginatedData = computed(() => {
  if (props.isAsync) return props.data;
  const start = (currentPage.value - 1) * pageSize.value;
  return sortedData.value?.slice(start, start + pageSize.value);
});

const rawData = computed(() => props.data ?? []);

const totalRecords = computed(() => props.totalItems ?? 0);
const totalPages = computed(() => Math.ceil(totalRecords.value / pageSize.value));
const visiblePages = computed(() => {
  const start = Math.max(1, currentPage.value - 3);
  const end = Math.min(totalPages.value, start + 6);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const startIndex = computed(() => {
  if (totalRecords.value === 0) return 0;
  return (currentPage.value - 1) * pageSize.value;
});
const endIndex = computed(() => {
  if (totalRecords.value === 0) return 0;
  return Math.min(startIndex.value + pageSize.value, totalRecords.value);
});

const sort = (col: Column) => {
  if (!col.sortable) return;
  sortOrder.value = sortColumn.value === col.sortKey && sortOrder.value === 'asc' ? 'desc' : 'asc';
  sortColumn.value = col.sortKey ?? null;
  if (props.isAsync) {
    emit('fetch-data', {
      sortColumn: sortColumn.value,
      sortOrder: sortOrder.value,
      pageSize: pageSize.value,
      page: currentPage.value,
      searchQuery: searchQuery.value,
    });
  }
};
const getRenderer = (renderer: any, row: any, col?: Column) => {
  if (!renderer) return null;

  if (typeof renderer === 'function') {
    // call renderer directly and return the VNode
    return renderer(row[col?.key], row);
  }

  return renderer;
};
const getSortIcon = (key: string | undefined) => {
  if (sortColumn.value === key) {
    return sortOrder.value === 'asc' ? ArrowUpIcon : ArrowDownIcon;
  }
  return ArrowsUpDownIcon;
};

const changePage = (page: number) => {
  // Don't allow page changes while loading
  if (props.isLoading || props.isFetching) return;

  // Make sure we don't try to navigate to a page that doesn't exist
  const validPage = Math.min(page, totalPages.value || 1);
  currentPage.value = validPage > 0 ? validPage : 1;

  if (props.isAsync) {
    emit('fetch-data', {
      page: currentPage.value,
      pageSize: pageSize.value,
      sortColumn: sortColumn.value,
      sortOrder: sortOrder.value,
      searchQuery: searchQuery.value,
    });
  }
};

const updatePageSize = () => {
  // Don't allow page size changes while loading
  if (props.isLoading || props.isFetching) return;

  currentPage.value = 1;
  emit('fetch-data', {
    page: currentPage.value,
    pageSize: pageSize.value,
    sortColumn: sortColumn.value,
    sortOrder: sortOrder.value,
    searchQuery: searchQuery.value,
  });
};

const handleCellClick = (event: MouseEvent, row: any, col: Column) => {
  // Check if the click was on a button or interactive element
  const target = event.target as HTMLElement;
  const isButtonClick =
    target.closest('button') !== null ||
    target.closest('select') !== null ||
    target.closest('a') !== null ||
    target.closest('[role="button"]') !== null;

  if (isButtonClick) {
    // Let the button handle its own click
    return;
  }

  // For non-button clicks, emit the cell-click event
  emit('cell-click', { row, column: col });

  // Ripple effect
  const cell = event.currentTarget as HTMLElement;
  const ripple = cell.querySelector('.ripple') as HTMLElement;
  if (ripple) {
    ripple.classList.remove('show');
    // Get click coordinates relative to the cell
    const rect = cell.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = event.clientX - rect.left - size / 2 + 'px';
    ripple.style.top = event.clientY - rect.top - size / 2 + 'px';
    // Trigger reflow to restart animation
    void ripple.offsetWidth;
    ripple.classList.add('show');
  }
};
</script>

<style scoped>
.ripple {
  position: absolute;
  border-radius: 50%;
  transform: scale(0);
  opacity: 0.75;
  pointer-events: none;
  background-color: rgba(185, 185, 185, 0.3);
  z-index: 2;
  transition: none;
}

.ripple.show {
  animation: ripple 0.6s linear;
}

@keyframes ripple {
  to {
    transform: scale(4);
    opacity: 0;
  }
}

td {
  height: 100%;
}
</style>
